/**
 * Account Reset section — UI wrapper for reset_email_account.py.
 *
 * Allows selecting an email account, previewing deletion counts,
 * and executing the reset with optional EveMailer trigger.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import {
    AlertTriangle,
    CheckCircle,
    RefreshCw05,
    SearchLg,
    Trash01,
    XCircle,
} from "@untitledui/icons";
import { Button } from "@/components/base/buttons/button";
import { Badge } from "@/components/base/badges/badges";
import { useQuery } from "@tanstack/react-query";
import { apiGet, apiPost } from "@/lib/api-client";
import { storage } from "@/lib/local-storage";
import { cx } from "@/utils/cx";

// ============================================================================
// Types
// ============================================================================

interface EmailAccountItem {
    account_id: string;
    email_address: string;
    display_name: string | null;
    status: string;
    provider: string;
    last_sync_at: string | null;
    company_id: string;
    company_name: string;
}

interface PreviewResult {
    account: {
        account_id: string;
        email: string;
        status: string;
        company_id: string;
        company_name: string;
        last_sync_at: string | null;
    };
    email_counts: Record<string, number>;
    total_emails: number;
    reprocess_count: number;
    table_counts: Record<string, number>;
    total_rows_to_delete: number;
}

interface LogEntry {
    type: string;
    message?: string;
    step?: number;
    label?: string;
    status?: string;
    table?: string;
    rows?: number;
    index?: number;
    total?: number;
    message_id?: string;
    error?: string;
    summary?: Record<string, unknown>;
}

// ============================================================================
// Component
// ============================================================================

export function AccountResetSection() {
    // Search & selection
    const [search, setSearch] = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");
    const [selectedAccountId, setSelectedAccountId] = useState<string | null>(null);

    // Preview state
    const [preview, setPreview] = useState<PreviewResult | null>(null);
    const [isPreviewing, setIsPreviewing] = useState(false);
    const [previewError, setPreviewError] = useState<string | null>(null);

    // Execute state
    const [triggerMailer, setTriggerMailer] = useState(false);
    const [skipTracking, setSkipTracking] = useState(true);
    const [isExecuting, setIsExecuting] = useState(false);
    const [showConfirm, setShowConfirm] = useState(false);
    const [logs, setLogs] = useState<LogEntry[]>([]);
    const [executionComplete, setExecutionComplete] = useState(false);
    const abortRef = useRef<AbortController | null>(null);
    const logEndRef = useRef<HTMLDivElement>(null);

    // Debounce search
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
        return () => clearTimeout(t);
    }, [search]);

    // Fetch accounts
    const { data: accounts = [] } = useQuery<EmailAccountItem[]>({
        queryKey: ["admin", "debug", "email-accounts", debouncedSearch],
        queryFn: () =>
            apiGet("/admin/debug/email-accounts", {
                params: debouncedSearch ? { search: debouncedSearch } : undefined,
            }),
    });

    // Auto-scroll logs
    useEffect(() => {
        logEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [logs]);

    // Selected account info (from accounts list or preview)
    const selectedAccount = accounts.find((a) => a.account_id === selectedAccountId);

    const handleSelectAccount = useCallback((accountId: string) => {
        setSelectedAccountId(accountId);
        setPreview(null);
        setPreviewError(null);
        setLogs([]);
        setExecutionComplete(false);
    }, []);

    const handlePreview = useCallback(async () => {
        if (!selectedAccountId) return;
        setIsPreviewing(true);
        setPreviewError(null);
        try {
            const result = await apiPost("/admin/debug/reset-account/preview", {
                account_id: selectedAccountId,
            });
            setPreview(result as PreviewResult);
        } catch (err: unknown) {
            setPreviewError(err instanceof Error ? err.message : "Preview failed");
        } finally {
            setIsPreviewing(false);
        }
    }, [selectedAccountId]);

    const handleExecute = useCallback(async () => {
        if (!selectedAccountId) return;
        setShowConfirm(false);
        setIsExecuting(true);
        setLogs([]);
        setExecutionComplete(false);

        const abort = new AbortController();
        abortRef.current = abort;

        try {
            const token = storage.getItem("access_token") ?? "";
            const baseUrl = import.meta.env.VITE_API_BASE_URL || "http://localhost:8000";
            const versionPrefix = import.meta.env.VITE_API_VERSION_PREFIX || "/api/v1";
            const res = await fetch(`${baseUrl}${versionPrefix}/admin/debug/reset-account/execute`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    account_id: selectedAccountId,
                    trigger: triggerMailer,
                    skip_tracking: skipTracking,
                }),
                signal: abort.signal,
            });

            if (!res.ok) {
                const text = await res.text();
                setLogs((prev) => [...prev, { type: "error", message: `HTTP ${res.status}: ${text}` }]);
                setIsExecuting(false);
                return;
            }

            // Try SSE streaming first, fall back to full-body parse
            const reader = res.body?.getReader();
            if (!reader) {
                // No streaming support — parse full response
                const text = await res.text();
                const events = text.split("\n").filter((l) => l.startsWith("data: ")).map((l) => {
                    try { return JSON.parse(l.slice(6)) as LogEntry; } catch { return null; }
                }).filter(Boolean) as LogEntry[];
                setLogs(events);
                if (events.some((e) => e.type === "complete" || e.type === "error")) {
                    setExecutionComplete(true);
                }
                return;
            }

            const decoder = new TextDecoder();
            let buffer = "";

            while (true) {
                const { done, value } = await reader.read();
                if (done) break;
                buffer += decoder.decode(value, { stream: true });

                const lines = buffer.split("\n");
                buffer = lines.pop() ?? "";

                for (const line of lines) {
                    if (line.startsWith("data: ")) {
                        try {
                            const event: LogEntry = JSON.parse(line.slice(6));
                            setLogs((prev) => [...prev, event]);
                            if (event.type === "complete" || event.type === "error") {
                                setExecutionComplete(true);
                            }
                        } catch {
                            // skip malformed lines
                        }
                    }
                }
            }

            // Flush remaining buffer
            if (buffer.trim()) {
                for (const line of buffer.split("\n")) {
                    if (line.startsWith("data: ")) {
                        try {
                            const event: LogEntry = JSON.parse(line.slice(6));
                            setLogs((prev) => [...prev, event]);
                            if (event.type === "complete" || event.type === "error") {
                                setExecutionComplete(true);
                            }
                        } catch { /* skip */ }
                    }
                }
            }
        } catch (err: unknown) {
            if ((err as Error).name !== "AbortError") {
                setLogs((prev) => [...prev, { type: "error", message: (err as Error).message }]);
            }
        } finally {
            setIsExecuting(false);
            abortRef.current = null;
        }
    }, [selectedAccountId, triggerMailer]);

    const handleAbort = useCallback(() => {
        abortRef.current?.abort();
    }, []);

    return (
        <div className="flex flex-col gap-4 flex-1 min-h-0">
            {/* Description + search */}
            <div className="flex items-start gap-4">
                <div className="flex-1 rounded-lg border border-secondary bg-secondary/20 px-4 py-3">
                    <h4 className="text-sm font-semibold text-primary mb-1.5">What does Account Reset do?</h4>
                    <ol className="text-xs text-secondary space-y-1 list-decimal list-inside">
                        <li><strong>Deletes all derived data</strong> for the account&apos;s company: containers, documents, issues, review items, conversations, activities, risk history, bookings, folders, contacts, workflows &mdash; everything except raw emails.</li>
                        <li><strong>Resets emails to pending</strong>: all processed/pending/processing/failed emails go back to &ldquo;pending&rdquo;. Emails marked irrelevant, limbo, training, or user_rejected are left untouched.</li>
                        <li><strong>Fixes account status</strong>: sets the email account to &ldquo;active&rdquo; and clears last_sync_at.</li>
                        <li><strong>Trigger EveMailer</strong> (optional): re-processes all pending emails through the full pipeline from scratch &mdash; classification, extraction, container creation, etc.</li>
                    </ol>
                    <p className="text-xs text-tertiary mt-2">In short: <strong>tabula rasa</strong> of derived data, then full reprocessing from the original emails.</p>
                </div>
                <div className="relative w-80 shrink-0">
                    <SearchLg className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-tertiary pointer-events-none" />
                    <input
                        type="text"
                        placeholder="Search by email or company name..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-secondary bg-primary text-primary placeholder:text-tertiary focus:outline-none focus:ring-2 focus:ring-brand-secondary/50 focus:border-brand-secondary"
                    />
                </div>
            </div>

            <div className="rounded-lg border border-secondary bg-primary overflow-hidden flex-1 min-h-0">
                <div className="flex h-full">
                    {/* Left panel: account list */}
                    <div className="w-80 shrink-0 border-r border-secondary flex flex-col min-h-0">
                        <div className="px-4 py-3 border-b border-secondary shrink-0">
                            <h3 className="text-sm font-semibold text-primary">
                                Email Accounts
                                <span className="ml-2 text-tertiary font-normal">
                                    ({accounts.length})
                                </span>
                            </h3>
                        </div>
                        <div className="flex-1 overflow-y-auto min-h-0">
                            {accounts.length === 0 ? (
                                <div className="flex items-center justify-center p-6">
                                    <p className="text-tertiary text-sm text-center">
                                        {debouncedSearch ? "No accounts found" : "Loading accounts..."}
                                    </p>
                                </div>
                            ) : (
                                accounts.map((acc) => (
                                    <button
                                        key={acc.account_id}
                                        type="button"
                                        onClick={() => handleSelectAccount(acc.account_id)}
                                        className={cx(
                                            "w-full text-left px-4 py-3 border-b border-secondary transition-colors",
                                            selectedAccountId === acc.account_id
                                                ? "bg-brand-primary_alt"
                                                : "hover:bg-secondary/50",
                                        )}
                                    >
                                        <p className="text-sm font-medium text-primary truncate">
                                            {acc.email_address}
                                        </p>
                                        <div className="flex items-center gap-2 mt-1">
                                            <span className="text-xs text-tertiary truncate">
                                                {acc.company_name}
                                            </span>
                                            <Badge
                                                color={
                                                    acc.status === "active" ? "success"
                                                    : acc.status === "error" ? "error"
                                                    : "gray"
                                                }
                                                size="sm"
                                            >
                                                {acc.status}
                                            </Badge>
                                        </div>
                                    </button>
                                ))
                            )}
                        </div>
                    </div>

                    {/* Right panel: preview + execute */}
                    <div className="flex-1 flex flex-col min-h-0 min-w-0 p-4 overflow-y-auto gap-4">
                        {!selectedAccountId ? (
                            <div className="flex-1 flex items-center justify-center">
                                <p className="text-tertiary text-sm">Select an email account on the left to preview and execute a reset.</p>
                            </div>
                        ) : (
                            <>
                                {/* Account info card */}
                                {selectedAccount && (
                                    <div className="rounded-lg border border-secondary p-4 bg-secondary/20">
                                        <div className="flex items-center justify-between">
                                            <div>
                                                <h3 className="text-sm font-semibold text-primary">{selectedAccount.email_address}</h3>
                                                <p className="text-xs text-tertiary mt-0.5">
                                                    {selectedAccount.company_name} &middot; {selectedAccount.provider} &middot;{" "}
                                                    <Badge color={selectedAccount.status === "active" ? "success" : "gray"} size="sm">
                                                        {selectedAccount.status}
                                                    </Badge>
                                                </p>
                                                {selectedAccount.last_sync_at && (
                                                    <p className="text-xs text-tertiary mt-0.5">
                                                        Last sync: {new Date(selectedAccount.last_sync_at).toLocaleString()}
                                                    </p>
                                                )}
                                            </div>
                                            <Button
                                                color="secondary"
                                                size="sm"
                                                onPress={handlePreview}
                                                isLoading={isPreviewing}
                                                isDisabled={isExecuting}
                                            >
                                                <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><SearchLg className="size-4 shrink-0" /> Preview Reset</span>
                                            </Button>
                                        </div>
                                    </div>
                                )}

                                {previewError && (
                                    <div className="flex items-center gap-2 text-sm text-error-primary">
                                        <AlertTriangle className="size-4 shrink-0" />
                                        {previewError}
                                    </div>
                                )}

                                {/* Preview results */}
                                {preview && (
                                    <>
                                        {/* Email counts */}
                                        <div className="rounded-lg border border-secondary p-4">
                                            <h4 className="text-sm font-semibold text-primary mb-2">
                                                Email Status Breakdown
                                                <span className="ml-2 text-tertiary font-normal">
                                                    ({preview.total_emails} total, {preview.reprocess_count} to reprocess)
                                                </span>
                                            </h4>
                                            <div className="flex flex-wrap gap-2">
                                                {Object.entries(preview.email_counts).map(([status, count]) => (
                                                    <div key={status} className="flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-secondary/50">
                                                        <span className="text-xs font-medium text-primary">{status}</span>
                                                        <span className="text-xs text-tertiary">{count}</span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Table counts */}
                                        <div className="rounded-lg border border-secondary p-4">
                                            <h4 className="text-sm font-semibold text-primary mb-2">
                                                Data to Delete
                                                <span className="ml-2 text-tertiary font-normal">
                                                    (~{preview.total_rows_to_delete.toLocaleString()} rows)
                                                </span>
                                            </h4>
                                            <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                                                {Object.entries(preview.table_counts)
                                                    .sort(([, a], [, b]) => b - a)
                                                    .map(([table, count]) => (
                                                        <div key={table} className="flex items-center justify-between text-xs py-0.5">
                                                            <span className="text-secondary font-mono">{table}</span>
                                                            <span className="text-primary font-medium">{count.toLocaleString()}</span>
                                                        </div>
                                                    ))}
                                            </div>
                                        </div>

                                        {/* Execute controls */}
                                        <div className="rounded-lg border border-warning-secondary bg-warning-primary/5 p-4">
                                            <h4 className="text-sm font-semibold text-primary mb-3 flex items-center gap-2">
                                                <AlertTriangle className="size-4 text-warning-primary" />
                                                Execute Reset
                                            </h4>
                                            <div className="flex flex-col gap-2 mb-3">
                                                <label className="flex items-center gap-2 text-sm text-secondary cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={triggerMailer}
                                                        onChange={(e) => setTriggerMailer(e.target.checked)}
                                                        className="rounded border-secondary"
                                                        disabled={isExecuting}
                                                    />
                                                    Trigger EveMailer after reset
                                                </label>
                                                <label className="flex items-center gap-2 text-sm text-secondary cursor-pointer">
                                                    <input
                                                        type="checkbox"
                                                        checked={skipTracking}
                                                        onChange={(e) => setSkipTracking(e.target.checked)}
                                                        className="rounded border-secondary"
                                                        disabled={isExecuting}
                                                    />
                                                    Skip JSONCargo tracking (disable API calls during reprocessing)
                                                </label>
                                            </div>
                                            <div className="flex items-center gap-2">
                                                {!isExecuting ? (
                                                    !showConfirm ? (
                                                        <Button
                                                            color="primary-destructive"
                                                            size="sm"
                                                            onPress={() => setShowConfirm(true)}
                                                        >
                                                            <span className="inline-flex items-center gap-1.5 whitespace-nowrap"><Trash01 className="size-4 shrink-0" /> Execute Reset</span>
                                                        </Button>
                                                    ) : (
                                                        <>
                                                            <span className="text-sm text-warning-primary font-medium">
                                                                This will permanently delete all derived data. Are you sure?
                                                            </span>
                                                            <Button color="primary-destructive" size="sm" onPress={handleExecute}>
                                                                Yes, Execute
                                                            </Button>
                                                            <Button color="secondary" size="sm" onPress={() => setShowConfirm(false)}>
                                                                Cancel
                                                            </Button>
                                                        </>
                                                    )
                                                ) : (
                                                    <Button color="secondary-destructive" size="sm" onPress={handleAbort}>
                                                        Abort
                                                    </Button>
                                                )}
                                            </div>
                                        </div>
                                    </>
                                )}

                                {/* Execution log */}
                                {logs.length > 0 && (
                                    <div className="rounded-lg border border-secondary p-4">
                                        <h4 className="text-sm font-semibold text-primary mb-2 flex items-center gap-2">
                                            {isExecuting ? (
                                                <RefreshCw05 className="size-4 text-brand-secondary animate-spin" />
                                            ) : executionComplete ? (
                                                <CheckCircle className="size-4 text-success-primary" />
                                            ) : (
                                                <XCircle className="size-4 text-error-primary" />
                                            )}
                                            Execution Log
                                        </h4>
                                        <div className="font-mono text-xs space-y-0.5 max-h-80 overflow-y-auto bg-secondary/30 rounded p-3">
                                            {logs.map((entry, i) => (
                                                <LogLine key={i} entry={entry} />
                                            ))}
                                            <div ref={logEndRef} />
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

// ============================================================================
// Log Line sub-component
// ============================================================================

function LogLine({ entry }: { entry: LogEntry }) {
    if (entry.type === "info") {
        return <div className="text-secondary">{entry.message}</div>;
    }
    if (entry.type === "step") {
        const icon = entry.status === "start" ? ">" : entry.status === "done" ? "\u2713" : "?";
        const color = entry.status === "done" ? "text-success-primary" : "text-brand-secondary";
        return (
            <div className={color}>
                {icon} Step {entry.step}: {entry.label}
            </div>
        );
    }
    if (entry.type === "delete") {
        return (
            <div className="text-tertiary">
                &nbsp;&nbsp;- {entry.table}: {entry.rows} rows deleted
            </div>
        );
    }
    if (entry.type === "email_progress") {
        const color = entry.status === "ok" ? "text-success-primary" : "text-error-primary";
        return (
            <div className={color}>
                &nbsp;&nbsp;[{entry.index}/{entry.total}] {entry.message_id?.slice(0, 8)}... {entry.status}
                {entry.error && <span className="text-error-primary ml-2">({entry.error})</span>}
            </div>
        );
    }
    if (entry.type === "complete") {
        return (
            <div className="text-success-primary font-semibold mt-1">
                {"\u2713"} Reset complete — {JSON.stringify(entry.summary)}
            </div>
        );
    }
    if (entry.type === "error") {
        return (
            <div className="text-error-primary">
                {"\u2717"} Error: {entry.message}
            </div>
        );
    }
    return <div className="text-tertiary">{JSON.stringify(entry)}</div>;
}
